import * as React from "react";
import { CodeEditor } from "../group/identityMatcher/codeEditor";
import { Layout } from "../../layout";
import { parentLink, request, resourceLink } from "../../services/cpln";
import { Terminal } from "react-feather";
import { Upload, notification, Modal, Button, Switch, Alert, Checkbox, AutoComplete } from "antd";
import { ImportOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Loader } from "../../components/layout/loader";
import { InfoTooltip } from "../../components/InfoTooltip";
import { DOCS_URL } from "../../envVariables";
import { kindMetadata } from "../../services/kindMetadata";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { FormLabel } from "../../components/antd/form/FormLabel";
import { selectContext } from "../../store/contextSlice";
import { applyActions } from "../../store/applySlice";
import { addNotification } from "../../store/notificationDrawerSlice";
import jsYaml from "js-yaml";
import { useDebounce } from "../../components/table/useDebounce";

const STORAGE_KEY_DONT_SHOW_MODAL = "CPLN_APPLY_DONT_SHOW_RENAME_WARNING_MODAL";
export const CPLNApply = () => {
  const dispatch = useAppDispatch();
  const context = useAppSelector(selectContext);
  const theme = useAppSelector((state) => state.theme.theme);

  const navigate = useNavigate();

  const orgName = useAppSelector((state) => state.apply.orgName);
  const [debouncedOrgName, setDebouncedOrgName] = useDebounce(orgName, 200);
  React.useEffect(() => {
    setDebouncedOrgName(orgName);
  }, [orgName]);
  const [orgOptions, setOrgOptions] = React.useState<{ label: string; value: string }[]>([]);
  React.useEffect(() => {
    fetchOrgOptions();
  }, []);

  const gvcName = useAppSelector((state) => state.apply.gvcName);
  const [gvcOptions, setGvcOptions] = React.useState<{ label: string; value: string }[]>([]);
  React.useEffect(() => {
    setGvcOptions([]);
    fetchGvcOptions();
  }, [debouncedOrgName]);

  async function fetchOrgOptions() {
    try {
      const { data } = await request({ url: `/org` });
      setOrgOptions(data.items.map((i) => ({ label: i.name, value: i.name })));
    } catch (e) {
      setOrgOptions([]);
    }
  }

  async function fetchGvcOptions() {
    try {
      const { data } = await request({ url: `/org/${debouncedOrgName}/gvc` });
      setGvcOptions(data.items.map((i) => ({ label: i.name, value: i.name })));
    } catch (e) {
      setGvcOptions([]);
    }
  }

  const isLoading = useAppSelector((state) => state.apply.isLoading);
  const type = useAppSelector((state) => state.apply.type);
  const language = useAppSelector((state) => state.apply.language);
  const content = useAppSelector((state) => state.apply.content);
  const error = useAppSelector((state) => state.apply.error);
  const previousPath = useAppSelector((state) => state.apply.previousPath);
  const originalResource = useAppSelector((state) => state.apply.originalResource);

  const [isDragging, setIsDragging] = React.useState(false);
  const [isRenamingResource, setIsRenamingResource] = React.useState(false);
  const [isRenamingOrg, setIsRenamingOrg] = React.useState(false);
  const [dontShowModalAgain, setDontShowModalAgain] = React.useState(false);
  const [isUploadDragOver, setIsUploadDragOver] = React.useState(false);
  const uploadDragCounter = React.useRef(0);
  const page = typeof window !== "undefined" ? window.location.pathname : "";

  function closeModal() {
    setIsRenamingResource(false);
  }

  function closeOrgModal() {
    setIsRenamingOrg(false);
  }

  async function onApply() {
    // Perform a straight apply only if:
    // - User requested to not show the rename warning again, or
    // - User requested to perform a delete request, or
    // - There is no resource being edited, or
    // - The user is not applying 1 resource, or
    // - Org or GVC are different

    const objects: any = jsYaml.loadAll(content, null, { json: true });

    if (
      localStorage.getItem(STORAGE_KEY_DONT_SHOW_MODAL) === "true" ||
      type === "delete" ||
      !originalResource ||
      objects.length !== 1 ||
      orgName !== originalResource.org ||
      (originalResource.gvc && gvcName !== originalResource.gvc)
    ) {
      await onConfirmApply();
      return;
    }

    // Identify the resource that is being edited
    let userEditedResource: any = objects[0];

    // If the userEditedResource is invalid, let's ignore and apply as usual
    if (!userEditedResource || !userEditedResource.kind || !userEditedResource.name) {
      await onConfirmApply();
      return;
    }

    // We assume that the user changed the name of the resource on edit if the kinds match but the names do not;
    // in this case, let's show the user an informational modal

    // Special case for orgs - they cannot be renamed or cloned
    if (userEditedResource.kind === "org" && userEditedResource.name !== originalResource.name) {
      setIsRenamingOrg(true);
      return;
    }

    if (userEditedResource.kind === originalResource.kind && userEditedResource.name !== originalResource.name) {
      setIsRenamingResource(true);
      return;
    }

    // If we got here, then the kinds and names match OR the user decided to apply a different kind
    await onConfirmApply();
  }

  async function confirmModal(): Promise<void> {
    if (dontShowModalAgain) {
      localStorage.setItem(STORAGE_KEY_DONT_SHOW_MODAL, "true");
    }

    closeModal();
    await onConfirmApply();
  }

  // apply list and query result
  // apply array as []
  // apply array as ---
  // apply single item
  // apply a mix of them
  async function onConfirmApply() {
    dispatch(applyActions.setIsApplying(true));
    const failures: string[] = [];
    const objects: any = jsYaml.loadAll(content, null, { json: true });

    for (let obj of objects) {
      const isApiList = ["list", "queryresult"].includes(obj.kind);
      const isArray = Array.isArray(obj);
      if (isApiList) {
        for (let item of obj.items) {
          try {
            await applyItem(item);
          } catch (e) {
            let errorMessage = e?.response?.data?.message;
            if (!errorMessage) errorMessage = e.message;
            failures.push(errorMessage);
          }
        }
      } else if (isArray) {
        for (let item of obj) {
          try {
            await applyItem(item);
          } catch (e) {
            let errorMessage = e?.response?.data?.message;
            if (!errorMessage) errorMessage = e.message;
            failures.push(errorMessage);
          }
        }
      } else {
        try {
          await applyItem(obj);
        } catch (e) {
          let errorMessage = e?.response?.data?.message;
          if (!errorMessage) errorMessage = e.message;
          failures.push(errorMessage);
        }
      }
    }

    dispatch(applyActions.setIsApplying(false));

    if (failures.length > 0) {
      notification.warning({
        message: "Failure",
        description: (
          <div className="flex flex-col">
            <div>Some items failed</div>
            <ul>
              {failures.map((f) => (
                <li>{f}</li>
              ))}
            </ul>
          </div>
        ),
      });
    } else {
      notification.success({
        message: "Success",
        description: "Applied all data",
      });
      if (objects.length === 1 && type !== "delete") {
        const itemKind = objects[0].kind;
        if (!["org", "gvc"].includes(itemKind)) {
          let link = resourceLink(itemKind, objects[0].name, {
            org: orgName || context.org,
            gvc: objects[0].gvc || gvcName || context.gvc,
          });
          navigate(`/console${link}`);
          return;
        }
      }
      setTimeout(() => {
        window.location.pathname = "/console";
      }, 100);
    }
  }

  async function applyItem(item: any) {
    let link = "";
    try {
      link = resourceLink(item.kind, item.name, {
        org: orgName || context.org,
        gvc: item.gvc || gvcName || context.gvc,
      });
    } catch (e) {
      // TODO make this a constant
      if (e.message === "context-gvc") {
        throw new Error(`Item "${item.name}" of kind "${item.kind}" requires a gvc.`);
      }
      throw new Error("Not a valid item");
    }

    if (type === "delete") {
      return await request({ method: "delete", url: link, body: item });
    }
    // TODO check if works
    link = parentLink(item.kind, {
      org: orgName || context.org,
      gvc: item.gvc || gvcName || context.gvc,
    });

    return await request({ method: "put", url: link, body: item });
  }

  function getFirstObjectName(): string {
    try {
      const objects: any = jsYaml.loadAll(content, null, { json: true });

      if (!objects || objects.length === 0 || !objects[0].name) {
        return "";
      }

      return objects[0].name;
    } catch (e) {
      return "";
    }
  }

  function handleDragOver(event: React.DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setIsDragging(true);
  }

  function handleDragLeave(event: React.DragEvent<HTMLDivElement>): void {
    setIsDragging(false);
  }

  async function handleDrop(event: React.DragEvent<HTMLDivElement>): Promise<void> {
    event.preventDefault();
    setIsDragging(false);

    const files: FileList = event.dataTransfer.files;

    if (files.length === 0) {
      dispatch(
        addNotification({
          page,
          action: "Import YAML/JSON",
          success: false,
          message: "No files were detected in the drop",
        })
      );
      return;
    }

    const file = files[0];
    try {
      const text = await file.text();
      if (!text || text.trim().length === 0) {
        dispatch(
          addNotification({
            page,
            action: "Import YAML/JSON",
            success: false,
            message: "File is empty",
          })
        );
        return;
      }
      dispatch(applyActions.setContent(text));
      dispatch(
        addNotification({
          page,
          action: "Import YAML/JSON",
          success: true,
          message: "File imported successfully",
          details: { fileName: file.name },
        })
      );
    } catch (e) {
      dispatch(
        addNotification({
          page,
          action: "Import YAML/JSON",
          success: false,
          message: `Failed to read file: ${(e as Error).message}`,
        })
      );
    }
  }

  // Computed
  const userEditedResourceName: string = getFirstObjectName();

  if (isLoading) {
    return (
      <Layout showLeftMenu={false}>
        <Loader />
      </Layout>
    );
  }

  return (
    <Layout showLeftMenu={false}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.625rem" }}>
        <Terminal color="var(--color-label)" size={20} />
        <span style={{ color: "var(--color-label)" }}>cpln apply</span>
      </div>
      <div className="mb-4 my-2 flex flex-col gap-2">
        <span>Automate modifications to the system using JSON or YAML input.</span>
        <span>
          Functions the same as the CLI's{" "}
          <a className="ngfocus color-link" href={`${DOCS_URL}/reference/cli#apply`} target={"_blank"}>
            cpln apply
          </a>{" "}
          command.
        </span>
        <span>
          An existing item with the specified name is replaced with the YAML, or a new item is created if it doesn't
          exist.
        </span>
        <div className="w-full border-b" style={{ height: 1 }} />
      </div>
      <div className="flex items-center mb-4 gap-6">
        <div className="inline-flex items-center">
          <Upload
            accept=".json,.yaml,.yml"
            showUploadList={false}
            beforeUpload={async (file: File) => {
              try {
                const text = await file.text();
                if (!text || text.trim().length === 0) {
                  dispatch(
                    addNotification({
                      page,
                      action: "Import YAML/JSON",
                      success: false,
                      message: "File is empty",
                    })
                  );
                  return false;
                }
                dispatch(applyActions.setContent(text));
                dispatch(
                  addNotification({
                    page,
                    action: "Import YAML/JSON",
                    success: true,
                    message: "File imported successfully",
                    details: { fileName: file.name },
                  })
                );
              } catch (e) {
                dispatch(
                  addNotification({
                    page,
                    action: "Import YAML/JSON",
                    success: false,
                    message: `Failed to read file: ${(e as Error).message}`,
                  })
                );
              }
              return false;
            }}
            className="inline-block [&_.ant-upload]:block"
          >
            <div
              className="flex items-center gap-2 px-3 py-1.5 border border-dashed rounded cursor-pointer transition-all text-sm"
              style={{
                borderColor: isUploadDragOver ? "var(--color-primary)" : "var(--color-border)",
                color: isUploadDragOver ? "var(--color-primary)" : "var(--color-label)",
                backgroundColor: isUploadDragOver ? "var(--color-primary-faint)" : "transparent",
              }}
              onMouseEnter={(e) => {
                if (!isUploadDragOver) {
                  e.currentTarget.style.borderColor = "var(--color-primary)";
                  e.currentTarget.style.color = "var(--color-primary)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isUploadDragOver) {
                  e.currentTarget.style.borderColor = "var(--color-border)";
                  e.currentTarget.style.color = "var(--color-label)";
                }
              }}
              onDragEnter={(e) => {
                e.preventDefault();
                uploadDragCounter.current++;
                setIsUploadDragOver(true);
              }}
              onDragOver={(e) => e.preventDefault()}
              onDragLeave={() => {
                uploadDragCounter.current--;
                if (uploadDragCounter.current === 0) setIsUploadDragOver(false);
              }}
              onDrop={() => {
                uploadDragCounter.current = 0;
                setIsUploadDragOver(false);
              }}
            >
              <ImportOutlined />
              <span>Drop file or click to import</span>
            </div>
          </Upload>
          <InfoTooltip
            title={
              <div className="text-xs">
                <div className="font-semibold mb-1">Accepted formats:</div>
                <div><strong>.yaml, .yml</strong> — YAML resource definitions</div>
                <div><strong>.json</strong> — JSON resource definitions</div>
                <div className="mt-1 opacity-75">Supports single resources and arrays</div>
              </div>
            }
            style={{ marginLeft: 8 }}
          />
        </div>
        <div className="flex items-center gap-2">
          <FormLabel>Org</FormLabel>
          <AutoComplete
            value={orgName}
            onChange={(value) => dispatch(applyActions.setOrgName(value))}
            style={{ width: 200 }}
            options={orgOptions}
          />
        </div>
        <div className="flex items-center gap-2">
          <FormLabel>GVC</FormLabel>
          <AutoComplete
            value={gvcName}
            onChange={(value) => dispatch(applyActions.setGvcName(value))}
            style={{ width: 200 }}
            options={gvcOptions}
          />
        </div>
        <div className="flex items-center">
          <div className="flex items-center gap-1">
            <label htmlFor="use-as-delete">Use as Delete</label>
            <Switch
              id={"use-as-delete"}
              value={type === "delete"}
              onChange={(value) => dispatch(applyActions.setType(value ? "delete" : "put"))}
            />
          </div>
          <InfoTooltip
            title="When enabled, instead of updating/creating the items, it deletes them by sending the HTTP requests with DELETE method."
            styles={{ root: { maxWidth: 450 } }}
          />
        </div>
      </div>
      <div
        className="relative rounded"
        style={{
          outline: isDragging ? "2px dashed var(--color-primary)" : "none",
          outlineOffset: -2,
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragging && (
          <div
            className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none rounded"
            style={{ backgroundColor: "var(--color-primary-faint)" }}
          >
            <span style={{ color: "var(--color-primary)" }}>Drop file to import</span>
          </div>
        )}
        <CodeEditor
          delay
          lint
          value={content}
          setValue={(value) => dispatch(applyActions.setContent(value))}
          language={language}
          options={{ basicSetup: { lineNumbers: true, autocompletion: false } }}
        />
        {content.length == 0 && !isDragging ? (
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none select-none text-2xl"
            style={{ color: theme === "dark" ? `var(--color-gray-800)` : `var(--color-gray-400)` }}
          >
            Write or Drag & Drop YAML or JSON File
          </div>
        ) : null}
      </div>
      {error ? <Alert showIcon className="mt-2" type={"error"} message={error} /> : null}
      <div className="flex items-center justify-end" style={{ marginTop: "2rem" }}>
        <Button
          style={{ width: 215, marginRight: 10 }}
          type="default"
          onClick={() => (previousPath ? navigate(previousPath) : navigate("/console"))}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          style={{ width: 215, marginLeft: 10 }}
          type="primary"
          onClick={onApply}
          loading={isLoading}
          disabled={isLoading || !content || !!error}
        >
          Apply
        </Button>
      </div>

      {/* Modal for Resource Renaming Confirmation */}
      {originalResource ? (
        <Modal
          title={`Create a New ${kindMetadata[originalResource.kind].name}: ${userEditedResourceName}`}
          open={isRenamingResource}
          maskClosable={false}
          destroyOnHidden
          onCancel={closeModal}
          onOk={confirmModal}
          okText={`Confirm`}
        >
          <div className="flex flex-col gap-2">
            <span>
              {kindMetadata[originalResource.kind].pluralName} cannot be renamed. The resource "{originalResource.name}"
              will remain unchanged, and a new resource named "{userEditedResourceName}" will be created. Would you like
              to proceed?
            </span>
            <Checkbox checked={dontShowModalAgain} onChange={(e) => setDontShowModalAgain(e.target.checked)}>
              Don't show this again
            </Checkbox>
          </div>
        </Modal>
      ) : null}

      {/* Modal for Org Rename Attempt */}
      <Modal
        title="Organization Name Cannot Be Changed"
        open={isRenamingOrg}
        maskClosable={false}
        destroyOnHidden
        onCancel={closeOrgModal}
        footer={[
          <Button key="close" type="primary" onClick={closeOrgModal}>
            Close
          </Button>,
        ]}
      >
        <div className="flex flex-col gap-2">
          <span>
            Organization names cannot be changed or cloned. To create a new organization, please use the billing account
            interface.
          </span>
        </div>
      </Modal>
    </Layout>
  );
};
